import { FroelingData, KnxWeatherChannel, KnxWeatherSnapshot, ThermometerReading } from '../version.service';
import { knxName, knxReadingName } from '../shared/knx-names';
import { valveFloors } from './board-valves';

/**
 * Board chart ids (the Analyse board's catalogue). One id = one chart on the board; the same ids are
 * bound to the 📌 buttons of the dashboard's chart popups ([boardChart]), stored in a slot's charts and
 * written to a snapshot link ("#analyse?c=<id>,<id>…"):
 *
 *   solar.pvYield | solar.battery | solar.grid | solar.consumption | solar.all (📈 energy modal)
 *   thermo.<deviceId>             a KNX room (Ist, Soll, valve)
 *   froeling.boiler | .buffer | .water | .solarThermal | .outdoor | .pump | froeling.all (📈 heating modal)
 *   froeling.hydraulics           boiler, buffer, circuits Ist / Soll, DHW tank + buffer / DHW pump and burner (%)
 *   froeling.pumps | .solarCircuit | .combustion | .state   every pump (%), the solar circuit, exhaust / O₂ /
 *                                 draft fan, the boiler's state over time
 *   froeling.hc.<circuit name>    a heating circuit ("froeling.hc.Heizkreis 01")
 *   froeling.settings             no chart: the Fröling's current settings by component, the neutral installer hints
 *                                 and the change log over the window (board-settings.ts)
 *   pellets | zisterne | wx.<series> (a KNX weather channel, e.g. wx.knxga-8-0-5)
 *   valves.all                    every room's valve: heatmap + evaluation (board-valves.ts, valve-heatmap.ts)
 *   valves.<floor>                the valves of a floor, one line per room ("valves.E1")
 */

export type FroelingPart = 'boiler' | 'buffer' | 'water' | 'solarThermal' | 'outdoor' | 'pump' | 'all'
  | 'hydraulics' | 'pumps' | 'solarCircuit' | 'combustion' | 'state';
export type SolarPart = 'pvYield' | 'battery' | 'grid' | 'consumption' | 'all';

/** A parsed board chart id. */
export type BoardRef =
  | { kind: 'froeling'; part: FroelingPart }
  | { kind: 'hc'; name: string }
  | { kind: 'pellets' }
  | { kind: 'thermo'; deviceId: string }
  | { kind: 'solar'; part: SolarPart }
  | { kind: 'zisterne' }
  | { kind: 'wx'; series: string }
  /** floor null: all rooms (heatmap + evaluation); else one floor's rooms as lines. */
  | { kind: 'valves'; floor: string | null }
  /** No chart: the settings snapshot (live, never from the link) and its change log over the window. */
  | { kind: 'froelingSettings' };

/** The settings widget's id. */
export const FROELING_SETTINGS = 'froeling.settings';

const FROELING_PARTS: readonly FroelingPart[] = ['boiler', 'buffer', 'water', 'solarThermal', 'outdoor', 'pump', 'all',
  'hydraulics', 'pumps', 'solarCircuit', 'combustion', 'state'];
const SOLAR_PARTS: readonly SolarPart[] = ['pvYield', 'battery', 'grid', 'consumption', 'all'];

/** The chart an id names, or null for an unknown id (an old or mistyped link: skipped). */
export function parseBoardId(id: string): BoardRef | null {
  if (id === 'pellets') return { kind: 'pellets' };
  if (id === 'zisterne') return { kind: 'zisterne' };
  if (id === FROELING_SETTINGS) return { kind: 'froelingSettings' };
  if (id.startsWith('froeling.hc.') && id.length > 12) return { kind: 'hc', name: id.slice(12) };
  const dot = id.indexOf('.');
  if (dot < 0) return null;
  const head = id.slice(0, dot), rest = id.slice(dot + 1);
  if (!rest) return null;
  if (head === 'froeling') return (FROELING_PARTS as readonly string[]).includes(rest) ? { kind: 'froeling', part: rest as FroelingPart } : null;
  if (head === 'solar') return (SOLAR_PARTS as readonly string[]).includes(rest) ? { kind: 'solar', part: rest as SolarPart } : null;
  if (head === 'thermo') return { kind: 'thermo', deviceId: rest };
  if (head === 'wx') return { kind: 'wx', series: rest };
  if (head === 'valves') return { kind: 'valves', floor: rest === 'all' ? null : rest };
  return null;
}

/** Display metadata of a KNX weather quantity (as the weather cards: icon, i18n label, unit, decimals,
 *  order, line colour light / dark — never the set-point orange). */
export interface WxMeta { icon: string; label: string; unit: string; digits: number; order: number; light: string; dark: string; }
const WX: Record<string, WxMeta> = {
  outdoorTemperature: { icon: '🌡',  label: 'KNX_WX_OUTDOOR_TEMP', unit: '°C',  digits: 1, order: 0, light: '#e03131', dark: '#ff6b6b' },
  temperature:        { icon: '🌡',  label: 'KNX_WX_TEMP',         unit: '°C',  digits: 1, order: 1, light: '#d6336c', dark: '#f06595' },
  humidity:           { icon: '💧', label: 'KNX_WX_HUMIDITY',     unit: '%',   digits: 0, order: 2, light: '#1098ad', dark: '#3bc9db' },
  windSpeed:          { icon: '💨', label: 'KNX_WX_WIND',         unit: 'm/s', digits: 1, order: 3, light: '#0ca678', dark: '#38d9a9' },
  windGust:           { icon: '🌬', label: 'KNX_WX_GUST',         unit: 'm/s', digits: 1, order: 4, light: '#7048e8', dark: '#9775fa' },
  brightness:         { icon: '☀️', label: 'KNX_WX_BRIGHTNESS',   unit: 'lx',  digits: 0, order: 5, light: '#f59f00', dark: '#ffd43b' },
  pressure:           { icon: '⏲️', label: 'KNX_WX_PRESSURE',     unit: 'hPa', digits: 0, order: 6, light: '#495057', dark: '#ced4da' },
  percent:            { icon: '📐', label: 'KNX_WX_PERCENT',      unit: '%',   digits: 0, order: 7, light: '#5c7cfa', dark: '#91a7ff' },
  rain:               { icon: '🌧', label: 'KNX_WX_RAIN',         unit: '',    digits: 1, order: 7, light: '#1c7ed6', dark: '#4dabf7' },
  binary:             { icon: '🔘', label: 'KNX_WX_BINARY',       unit: '',    digits: 0, order: 8, light: '#1c7ed6', dark: '#4dabf7' },
  unknown:            { icon: '❔', label: 'KNX_WX_UNKNOWN',      unit: '',    digits: 1, order: 9, light: '#868e96', dark: '#adb5bd' },
};

export function wxMeta(ch: KnxWeatherChannel | null | undefined): WxMeta {
  return WX[ch?.quantity ?? 'unknown'] ?? WX['unknown'];
}

/** A 1-bit weather channel (rain, twilight …): charted as on / off. */
export function wxBinary(ch: KnxWeatherChannel | null | undefined): boolean {
  if (!ch) return false;
  return typeof ch.value === 'boolean' || /^1(\.|$)/.test(ch.dpt ?? '')
    || (ch.value == null && (ch.quantity === 'binary' || ch.quantity === 'rain'));
}

/** The live data a chart's title and data need (AppComponent's latest frames). */
export interface BoardLive {
  thermometers: ThermometerReading[];
  froeling: FroelingData | null;
  knxWeather: KnxWeatherSnapshot | null;
}

/** i18n lookup (TranslateService.instant). */
export type Tr = (key: string, params?: Record<string, unknown>) => string;

/** A heating circuit's name in the UI language, digits as reported, as on the Heizung card and in
 *  its 📈 chart (HeizungComponent.hcLabel): "Heizkreis 01" → "Heating Circuit 01"; a name without
 *  a number as reported. */
export function hcLabel(name: string, t: Tr): string {
  const m = /(\d+)/.exec(name);
  return m ? `${t('HEIZKREIS')} ${m[1]}` : name;
}

const FROELING_TITLE: Record<FroelingPart, [string, string]> = {
  boiler:       ['🔥', 'HEIZUNG_KESSEL'],
  buffer:       ['🛢️', 'HEIZUNG_PUFFER'],
  water:        ['🚿', 'HEIZUNG_WARMWASSER'],
  solarThermal: ['☀️', 'HEIZUNG_SOLAR'],
  outdoor:      ['🌤️', 'HEIZUNG_AUSSEN'],
  pump:         ['🔄', 'HEIZUNG_ZIRKPUMPE'],
  all:          ['📈', 'BOARD_ALL_HEATING'],
  hydraulics:   ['🧭', 'HYD_TITLE'],
  pumps:        ['⚙️', 'BOARD_PUMPS'],
  solarCircuit: ['☀️', 'BOARD_SOLAR_CIRCUIT'],
  combustion:   ['💨', 'BOARD_COMBUSTION'],
  state:        ['🚦', 'BOARD_BOILER_STATE'],
};
const SOLAR_TITLE: Record<SolarPart, [string, string]> = {
  pvYield:     ['☀️', 'PV_ERTRAG'],
  battery:     ['🔋', 'AKKU'],
  grid:        ['🔌', 'NETZBEZUG'],
  consumption: ['🏠', 'VERBRAUCH'],
  all:         ['📈', 'BOARD_ALL_ENERGY'],
};

/** Icon and title of a chart (translated; room / weather names from the live data when known). */
export function boardTitle(ref: BoardRef, live: BoardLive, t: Tr, lang: string): { icon: string; title: string } {
  switch (ref.kind) {
    case 'froeling': { const [icon, key] = FROELING_TITLE[ref.part]; return { icon, title: t(key) }; }
    case 'solar':    { const [icon, key] = SOLAR_TITLE[ref.part];    return { icon, title: t(key) }; }
    case 'hc':       return { icon: '🌡️', title: hcLabel(ref.name, t) };
    case 'pellets':  return { icon: '🪵', title: t('HEIZUNG_PELLET_CHART_TITLE') };
    case 'zisterne': return { icon: '💧', title: t('ZISTERNE_TITLE') };
    case 'thermo': {
      const r = live.thermometers.find(x => x.deviceId === ref.deviceId);
      return { icon: '🌡', title: r ? knxReadingName(r, lang) : ref.deviceId };
    }
    case 'wx': {
      const ch = live.knxWeather?.channels.find(c => c.series === ref.series);
      const m = wxMeta(ch);
      const ga = ch?.ga ?? ref.series.replace(/^knxga-/, '').replace(/-/g, '/');
      return { icon: m.icon, title: ch ? `${t(m.label)} (${ga})` : ga };
    }
    case 'froelingSettings': return { icon: '⚙️', title: t('FS_TITLE') };
    case 'valves':
      return { icon: '🔧', title: ref.floor ? t('BOARD_VALVES_FLOOR', { floor: knxName(ref.floor, lang, 'floor') }) : t('BOARD_VALVES_ALL') };
  }
}

/** One entry of the "add a chart" picker. */
export interface CatalogItem { id: string; icon: string; title: string; }
/** A group of the picker (Heizung, Räume, Ventile, Energie, Wetter, Zisterne); rooms come in floor sub-groups. */
export interface CatalogGroup { key: string; title: string; subgroups: { title: string | null; items: CatalogItem[] }[]; }

/** The picker's catalogue from the live data: heating circuits from the Fröling frame, KNX rooms from the
 *  thermometers frame (by floor, in the backend's order) and their valves (all, per floor), weather channels
 *  from the station. */
export function buildCatalog(live: BoardLive, t: Tr, lang: string): CatalogGroup[] {
  const item = (id: string): CatalogItem => {
    const ref = parseBoardId(id)!;
    return { id, ...boardTitle(ref, live, t, lang) };
  };
  const heating: CatalogItem[] = [
    ...(['boiler', 'buffer', 'water', 'solarThermal', 'outdoor', 'pump'] as const).map(p => item('froeling.' + p)),
    ...(live.froeling?.heatingCircuits ?? []).map(hc => item('froeling.hc.' + hc.name)),
    item('pellets'),
    ...(['hydraulics', 'pumps', 'combustion', 'state', 'solarCircuit'] as const).map(p => item('froeling.' + p)),
    item('froeling.all'),
    item(FROELING_SETTINGS),
  ];
  const floors = new Map<string, CatalogItem[]>();
  for (const r of live.thermometers) {
    if (r.source !== 'knx') continue;
    const f = r.floor ?? '';
    if (!floors.has(f)) floors.set(f, []);
    floors.get(f)!.push(item('thermo.' + r.deviceId));
  }
  // the valve overview once a room with a valve is known
  const valveFloorIds = valveFloors(live.thermometers);
  const valves = valveFloorIds.length ? [item('valves.all'), ...valveFloorIds.map(f => item('valves.' + f))] : [];
  const energy = (['pvYield', 'battery', 'grid', 'consumption', 'all'] as const).map(p => item('solar.' + p));
  const weather = [...(live.knxWeather?.channels ?? [])]
    .sort((a, b) => wxMeta(a).order - wxMeta(b).order || a.ga.localeCompare(b.ga, undefined, { numeric: true }))
    .map(ch => item('wx.' + ch.series));
  return [
    { key: 'heating', title: t('TAB_HEIZUNG'), subgroups: [{ title: null, items: heating }] },
    { key: 'rooms', title: t('BOARD_GROUP_ROOMS'),
      subgroups: [...floors].map(([floor, items]) => ({ title: floor ? knxName(floor, lang, 'floor') : t('KNX_NO_ROOM'), items })) },
    { key: 'valves', title: t('BOARD_GROUP_VALVES'), subgroups: [{ title: null, items: valves }] },
    { key: 'energy', title: t('TAB_ENERGIE'), subgroups: [{ title: null, items: energy }] },
    { key: 'weather', title: t('KNX_TAB_WEATHER'), subgroups: [{ title: null, items: weather }] },
    { key: 'cistern', title: t('ZISTERNE_TITLE'), subgroups: [{ title: null, items: [item('zisterne')] }] },
  ];
}
